import * as React from "react";
import { useState, useMemo, MouseEvent, ChangeEvent, Dispatch, SetStateAction } from "react";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableSortLabel from "@mui/material/TableSortLabel";
import Box from "@mui/material/Box";
import { visuallyHidden } from "@mui/utils";
import Toolbar from "@mui/material/Toolbar";
import { Button, Grid, Stack, TextField } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import Paper from "@mui/material/Paper";
import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TablePagination from "@mui/material/TablePagination";
import { getComparator, Order, stableSort } from "../../../utils/Table";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import Typography from "@mui/material/Typography";
import { IIntegrationTestConfiguration } from "../../../services/registry/IntegrationTestConfigurationService";

interface HeadCell {
  id: keyof IIntegrationTestConfiguration | string;
  label: string;
  align?: "inherit" | "left" | "center" | "right" | "justify";
  padding?: "normal" | "checkbox" | "none";
}

const headCells: readonly HeadCell[] = [
  {
    id: "clientBranch",
    label: "Client Branch",
    padding: "none",
  },
  {
    id: "backendImageId",
    label: "Backend Image",
  },
  {
    id: "activePackageId",
    label: "Package ID",
  },
  {
    id: "activeSchemaBranch",
    label: "Schema Branch",
  },
  {
    id: "activeSchemaVersion",
    label: "Schema Version",
  },
  {
    id: "actions",
    label: "Actions",
    align: "right",
  },
];

interface IntegrationTestConfigurationsTableHeadProps {
  onRequestSort: (event: MouseEvent<unknown>, property: keyof IIntegrationTestConfiguration | string) => void;
  order: Order;
  orderBy: string;
  rowCount: number;
}

const IntegrationTestConfigurationsTableHead = ({
  order,
  orderBy,
  onRequestSort,
}: IntegrationTestConfigurationsTableHeadProps) => {
  const createSortHandler = (property: keyof IIntegrationTestConfiguration | string) => (event: MouseEvent<unknown>) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox"></TableCell>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.align}
            padding={headCell.padding}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              <Typography fontWeight="bold">{headCell.label}</Typography>
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
};

export interface IntegrationTestConfigurationsTableProps {
  rows: IIntegrationTestConfiguration[];
  selectedItem: IIntegrationTestConfiguration | undefined;
  setSelectedItem: Dispatch<SetStateAction<IIntegrationTestConfiguration | undefined>>;
  onAddButtonClick: () => void;
  onEditButtonClick: () => void;
  onRemoveButtonClick: () => void;
}

export const IntegrationTestConfigurationsTable = ({
  rows,
  selectedItem,
  setSelectedItem,
  onAddButtonClick,
  onEditButtonClick,
  onRemoveButtonClick,
}: IntegrationTestConfigurationsTableProps) => {
  const [order, setOrder] = useState<Order>("desc");
  const [orderBy, setOrderBy] = useState<keyof IIntegrationTestConfiguration | string>("creationDate");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filterQuery, setFilterQuery] = useState<string>("");

  const requestSortHandler = (event: MouseEvent<unknown>, property: keyof IIntegrationTestConfiguration | string) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const pageChangeHandler = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const rowsPerPageChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      return row.clientBranch.toLowerCase().includes(filterQuery.toLowerCase());
    });
  }, [rows, filterQuery]);

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  const visibleRows = useMemo(() => {
    return stableSort(filteredRows, getComparator(order, orderBy)).slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage,
    );
  }, [filteredRows, order, orderBy, page, rowsPerPage]);

  const handleClick = (row: IIntegrationTestConfiguration, action: () => void) => {
    setSelectedItem(row);
    action();
  };

  const isSelected = (row: IIntegrationTestConfiguration, selectedItem: IIntegrationTestConfiguration | undefined) =>
    selectedItem !== undefined && row.clientBranch === selectedItem.clientBranch;

  return (
    <Box sx={{ width: "100%" }}>
      <Paper sx={{ width: "100%", mb: 2 }}>
        <Toolbar sx={{ pl: { sm: 2 }, pr: { xs: 1, sm: 1 }, pt: { xs: 1, sm: 1 } }}>
          <Grid container spacing={1}>
            <Grid container justifyContent={"flex-start"} size={10}>
              <Button variant="outlined" startIcon={<AddIcon />} onClick={onAddButtonClick}>
                Add
              </Button>
              <TextField
                id="outlined-search"
                label="Search integration test configurations"
                type="search"
                size="small"
                onChange={(e) => setFilterQuery(e.target.value)}
                sx={{ width: "360px", marginLeft: 1 }}
                value={filterQuery}
              />
            </Grid>
          </Grid>
        </Toolbar>
        <TableContainer>
          <Table sx={{ minWidth: 750 }} size="small">
            <IntegrationTestConfigurationsTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={requestSortHandler}
              rowCount={rows.length}
            />
            <TableBody>
              {visibleRows.map((row) => {
                return (
                  <TableRow
                    hover
                    role="checkbox"
                    onClick={() => setSelectedItem(row)}
                    tabIndex={-1}
                    key={row.clientBranch}
                    selected={isSelected(row, selectedItem)}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell padding="normal" />
                    <TableCell component="th" id={row.clientBranch} scope="row" padding="none">
                      {row.clientBranch}
                    </TableCell>
                    <TableCell align="left">{row.backendImageId || "-"}</TableCell>
                    <TableCell align="left">{row.activePackageId || "-"}</TableCell>
                    <TableCell align="left">{row.activeSchemaBranch || "-"}</TableCell>
                    <TableCell align="left">{row.activeSchemaVersion || "-"}</TableCell>
                    <TableCell>
                      <Stack spacing={1} direction="row" justifyContent="flex-end">
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<EditIcon />}
                          onClick={() => handleClick(row, onEditButtonClick)}
                          disabled={!row.clientBranch}
                        >
                          Edit
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<DeleteIcon />}
                          color="error"
                          onClick={() => handleClick(row, onRemoveButtonClick)}
                          disabled={!row.clientBranch}
                        >
                          Remove
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
              {emptyRows > 0 && (
                <TableRow style={{ height: 33 * emptyRows }}>
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={pageChangeHandler}
          onRowsPerPageChange={rowsPerPageChangeHandler}
        />
      </Paper>
    </Box>
  );
};
